import { Button, Group, Legend, PhoneInput, RadioButtons, SelectInput, Spinner, TextInput } from "components/common"
import { Col, Form, Row } from "antd"
import { forwardRef, useCallback, useEffect, useImperativeHandle, useMemo } from "react"
import { getCountryList, getStoreCacheData } from "utils"
import { useForm, useWatch } from "antd/lib/form/Form"
import {
  useLazyGetCheckEmailQuery,
  useLazyGetCheckPhoneNumberQuery,
  useLazyGetCitiesQuery,
  useLazyGetClientBuyingIntentQuery,
  useLazyGetClientRatingQuery,
  useLazyGetClientTypeQuery,
  useLazyGetCountriesQuery,
  useLazyGetLocalityQuery,
  useLazyGetOccupationQuery,
  useLazyGetUsersQuery,
} from "features/shared/api"

import AffiliateSection from "./affiliateSection"
import _ from "lodash"
import { getClientInitialValues } from "../clientForm/clientInitialValues"
import getClientValidationSchema from "../clientForm/clientValidationSchema"
import getPrivilege from "utils/privileges/privileges"
import { schemaRules } from "features/shared/utils"
import store from "store"
import theme from "utils/themeVars"
import { useSelector } from "react-redux"
import { genderList, clientClassOptions } from "constants/list"
import { useTranslation } from "react-i18next"
import { isValidEmail } from "utils"
import { getEmailRules, getPhoneRules } from "features/clients/clientFormsHelperFucntions"

const AddClientForm = forwardRef(({ preserve = false, onSubmit, clientAdditionSource = "phone" }, ref) => {
  const { t } = useTranslation()
  const [form] = useForm()
  const [getCountries, { data: countries, isFetching: isFetchCountry }] = useLazyGetCountriesQuery()
  const [getCities, { data: cities, isFetching: isFetchingCities }] = useLazyGetCitiesQuery()
  const [getLocalities, { data: localities, isFetching: isFetchingLocality }] = useLazyGetLocalityQuery()
  const [getOccupations, { data: occupations, isFetching: isFetchingOccupations }] = useLazyGetOccupationQuery()
  const [getClientBuyingIntents, { data: clientBuyingIntents }] = useLazyGetClientBuyingIntentQuery()
  const [getClientRatings, { data: clientRatings }] = useLazyGetClientRatingQuery()
  const [getClientTypesAndSubtypes, { data: clientCategoryData, isLoading: isLoadingClientTypes }] =
    useLazyGetClientTypeQuery()
  const [getUsers, { data: usersList, isFetching: isFetchingUsers }] = useLazyGetUsersQuery()
  const [checkPhone, { data: phoneData, isError: phoneError, isFetching: phoneValidationFetching }] =
    useLazyGetCheckPhoneNumberQuery()
  const [checkEmail, { data: emailData, isError: emailError, isFetching: isEmailCheckFetching }] =
    useLazyGetCheckEmailQuery()

  useImperativeHandle(
    ref,
    () => ({
      ...form,
    }),
    []
  )
  const countryWatch = useWatch("country", form)
  const cityWatch = useWatch("city", form)
  const clientClass = useWatch("clientClass", form)
  const clientTypeWatch = useWatch("clientType", form)
  const emailWatch = useWatch("email", form)
  const clientTypeSlug = clientCategoryData?.slugByClientTypeId?.[clientTypeWatch]
  const userPrivileges = useSelector(state => state.app.privileges)
  let addClientRules = schemaRules(getClientValidationSchema(false))

  const debounceEmailValidator = useCallback(
    _.debounce(value => {
      if (!value || !isValidEmail(value)) return
      checkEmail({ email: value })
        .unwrap()
        .then(() => form.validateFields(["email"]))
    }, 200),
    []
  )

  useEffect(() => {
    !countries && getCountries()
    !occupations && getOccupations()
    !clientBuyingIntents && getClientBuyingIntents()
    !clientRatings && getClientRatings()
    !clientCategoryData && getClientTypesAndSubtypes()
    !usersList && getUsers({ teamType: "sales", module: "clients", subModule: "changeLeadAllocation" })
  }, [])

  const directClientSubtypeValue = useMemo(
    () => clientCategoryData?.individualSubtypes?.find(s => s.slug === "direct_client")?.value ?? null,
    [clientCategoryData]
  )

  const isPhoneRequired = clientAdditionSource !== "email"
  const isEmailRequired = clientAdditionSource === "email"

  const renderEmailField = () => (
    <Row gutter={[64, 0]}>
      <Col span={12}>
        <Form.Item
          name="email"
          label={t("Email")}
          required={isEmailRequired}
          hasFeedback={!!emailWatch}
          rules={getEmailRules(isEmailRequired)}
        >
          <TextInput
            placeholder={t("Enter email")}
            onChange={e => {
              e.target.value && debounceEmailValidator(e.target.value)
            }}
          />
        </Form.Item>
      </Col>
    </Row>
  )

  return (
    <div>
      <Form
        form={form}
        layout="vertical"
        name="client-form"
        initialValues={getClientInitialValues()}
        onFinish={values => {
          onSubmit && onSubmit(values)
        }}
        scrollToFirstError
        preserve={preserve}
        colon={false}
      >
        <Legend title={t("Contact Information")} />

        {clientAdditionSource === "email" && renderEmailField()}

        <Form.List name="mobileNumber" rules={[]}>
          {(fields, { add, remove }) => (
            <Row gutter={[64, 0]}>
              {fields.map((field, index) => (
                <Col span={12} key={field.key}>
                  <Form.Item
                    required={isPhoneRequired}
                    hasFeedback
                    name={field.name}
                    validateTrigger={["onBlur"]}
                    {...field}
                    rules={[
                      ...getPhoneRules(isPhoneRequired),
                      {
                        validator: async (_, value) => {
                          const phoneApiData = getStoreCacheData("getCheckPhoneNumber", { cell: value })
                          if (!phoneError && phoneApiData?.exists && value === phoneApiData?.data?.number)
                            return Promise.reject(new Error(t("Phone number already exists")))
                          return Promise.resolve()
                        },
                      },
                    ]}
                  >
                    <PhoneInput
                      label={t("Mobile Number")}
                      required={isPhoneRequired}
                      onChange={e => {
                        form.setFields([{ name: ["mobileNumber", field.name], errors: [""] }])
                      }}
                      onBlur={e => {
                        const val = e.target.value
                        val &&
                          checkPhone({ cell: val.split(" ").join("") })
                            .unwrap()
                            .then(() => form.validateFields([["mobileNumber", field.name]]))
                      }}
                      component={
                        index > 0 && (
                          <Button
                            type="link"
                            icon="FiTrash2"
                            className="p-0"
                            onClick={() => remove(field.name)}
                            size="small"
                            style={{ color: theme["error-color"], height: "auto" }}
                          >
                            {t("Remove")}
                          </Button>
                        )
                      }
                    />
                  </Form.Item>
                </Col>
              ))}
              {fields.length < 5 && (
                <Col span={12}>
                  <Form.Item label={t("Add Another No.")}>
                    <Button
                      color={theme["gray600"]}
                      bordered
                      block
                      onClick={() => add()}
                      icon="MdAdd"
                      text={t("Add")}
                      style={{ borderStyle: "dashed", height: 48 }}
                    />
                  </Form.Item>
                </Col>
              )}
            </Row>
          )}
        </Form.List>

        {clientAdditionSource !== "email" && renderEmailField()}
        {isLoadingClientTypes ? (
          <Spinner size="medium" />
        ) : (
          <>
            <Legend title={t("Client Details")} />
            <Group className="mbe-24" template="repeat(2, 1fr)" gap="16px 64px">
              <Form.Item name="clientClass" label={t("Client Channel")}>
                <RadioButtons
                  options={clientClassOptions().map(e =>
                    !getPrivilege("can_assign_affiliate_to_new_client", userPrivileges) && e.value == "affiliate"
                      ? { ...e, disabled: true }
                      : e
                  )}
                />
              </Form.Item>

              <Form.Item name="clientType" label={t("Client Type")} required rules={addClientRules}>
                <RadioButtons
                  options={clientCategoryData?.clientTypes}
                  onChange={e => {
                    const clientTypeValue = e?.target?.value
                    const currentSlug = clientCategoryData?.slugByClientTypeId?.[clientTypeValue]
                    form.setFieldsValue({
                      clientType: clientTypeValue,
                      clientSubType: currentSlug === "individual" ? directClientSubtypeValue : null,
                    })
                  }}
                />
              </Form.Item>

              {clientTypeWatch && (
                <>
                  {clientClass === "affiliate" && (
                    <AffiliateSection rules={addClientRules} form={form} subModuleName="addClient" />
                  )}

                  <Form.Item name="clientSubType" label={t("Client Sub Type")} required rules={addClientRules}>
                    <SelectInput
                      placeholder={t("Select client sub type")}
                      options={
                        clientTypeSlug === "individual"
                          ? clientCategoryData?.individualSubtypes
                          : clientCategoryData?.companySubtypes
                      }
                    />
                  </Form.Item>

                  {clientTypeSlug === "individual" && (
                    <Form.Item name="name" label={t("Name")} required rules={addClientRules}>
                      <TextInput placeholder={t("Enter name")} />
                    </Form.Item>
                  )}

                  {clientTypeSlug === "company" && (
                    <Form.Item name="companyName" label={t("Company Name")} required rules={addClientRules}>
                      <TextInput placeholder={t("Enter company name")} />
                    </Form.Item>
                  )}

                  {clientTypeSlug === "company" && (
                    <Form.Item name="pocName" label={t("POC Name")} required rules={addClientRules}>
                      <TextInput placeholder={t("Enter POC name")} />
                    </Form.Item>
                  )}

                  <Form.Item name="gender" label={t("Gender")} required rules={addClientRules}>
                    <RadioButtons getKey={item => item.id} getValue={item => item.slug} options={genderList()} />
                  </Form.Item>

                  <Form.Item name="country" label={t("Country")} required rules={addClientRules}>
                    <SelectInput
                      placeholder={t("Please select a Country")}
                      options={getCountryList(countries)}
                      loading={isFetchCountry}
                      onChange={(_value, option) => {
                        form.setFieldsValue({ country: option, city: null })
                        getCities(option.id)
                      }}
                    />
                  </Form.Item>

                  <Form.Item name="city" label={t("City")} required rules={addClientRules}>
                    <SelectInput
                      showSearch
                      placeholder={t("Please select a City")}
                      options={cities}
                      disabled={!!countryWatch ? false : true}
                      onChange={(value, option) => {
                        form.setFieldsValue({ city: option, locality: null })
                        getLocalities(option.id)
                      }}
                    />
                  </Form.Item>

                  <Form.Item name="locality" label={t("Location")} rules={addClientRules}>
                    <SelectInput
                      showSearch
                      placeholder={t("Please select a location")}
                      options={localities}
                      disabled={!!cityWatch ? false : true}
                      onChange={(value, option) => {
                        form.setFieldsValue({ locality: option })
                      }}
                    />
                  </Form.Item>
                </>
              )}
            </Group>
          </>
        )}
      </Form>
    </div>
  )
})

export default AddClientForm
